import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { ConsumableDto } from "@/core/features/consumables/consumable.dto";
import { CreateConsumableDto } from "@/core/features/consumables/create-consumable.dto";
import { UpdateConsumableDto } from "@/core/features/consumables/update-consumable.dto";
import { OptionType } from "@/types/OptionType";
import { IRequest } from "@/interfaces/IRequest";
import { merge } from "lodash";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { ConsumableHistoryDto } from "@/core/features/consumables/consumable-history.dto";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { QualificationTotalsDto } from "@/core/features/qualifications/qualification-totals.dto";
import { StockRecordTotalsDto } from "@/core/features/stock-records/stock-record-totals.dto";
import { ArchivedEnum } from "@altertec_gparn/lib";
import { downloadPdfBlob } from "@/utils/fileUtils";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class ConsumablesResource extends ResourceBaseService<ConsumableDto, CreateConsumableDto, UpdateConsumableDto> {
  protected EntityDto = ConsumableDto;
  protected CreateEntityDto = CreateConsumableDto;
  protected UpdateEntityDto = UpdateConsumableDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/consumables");
  }

  async optionLoader(
    inputValue: string,
    includeArchived?: boolean,
    customRequest: IRequest = {}
  ): Promise<OptionType[]> {
    const request: IRequest = inputValue
      ? { search: inputValue, searchBy: ["id", "name", "reference", "externalReferences.externalReference"] }
      : {};
    request.sortBy = "name";
    if (includeArchived != null) {
      request.filters = {};
      Object.assign(request.filters, { archived: includeArchived ? ArchivedEnum.ALL : ArchivedEnum.ONLY_ACTIVE });
    }
    const { data } = await this.findAll(applyOptionLoaderPagination(merge(request, customRequest)));
    return data.map((value) => value.mapEntityToOptionType([value.toString()]));
  }

  async isDeletable(id: string): Promise<boolean> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/deletable",
          method: "GET",
          auth: true,
        })
      )
      .then((response) => response === "true");
  }

  async ensureRepair(id: string): Promise<ConsumableDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: `${this.resourceUrl}/${id}/ensure-repair`,
          method: "POST",
          auth: true,
          body: {},
        })
      )
      .then((response) => plainToInstance(ConsumableDto, response));
  }

  async getHistory(warehouseId: string, request: IRequest): Promise<FindManyResult<ConsumableHistoryDto>> {
    return this.apiHttpClient
      .request<null, FindManyResult<ConsumableHistoryDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/history/" + warehouseId,
          method: "GET",
          query: this.getQueryBuilder(request).query(),
          auth: true,
        })
      )
      .then((response) => {
        response.data = response.data.map((element) =>
          plainToInstance(ConsumableHistoryDto, element, { excludeExtraneousValues: true })
        );
        return response;
      });
  }

  async getHistoryTotal(warehouseId: string, request: IRequest): Promise<QualificationTotalsDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/history/" + warehouseId + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(StockRecordTotalsDto, result));
  }

  archive(id: string): Promise<ConsumableDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/archive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  unarchive(id: string): Promise<ConsumableDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/unarchive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  async exportQrCodes(request: IRequest): Promise<void> {
    const data = await this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/qr-codes",
        method: "GET",
        auth: true,
        query: request ? this.getQueryBuilder(request).query() : null,
      })
    );

    downloadPdfBlob(String(data), "Códigos QR");
  }

  async importConsumables(file: File): Promise<{ jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { jobId: string }>(
      HttpRequest.create<FormData>({
        url: this.resourceUrl + "/import",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async downloadImportTemplate(): Promise<void> {
    return this.downloadExcel(null, "/import/template", "plantilla-consumibles");
  }
}
